/**
 * Admin Dashboard Component
 * Sidebar layout inspired by the provided design
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { useToast } from '../context/ToastContext';
import { LayoutDashboard, Package, Users, DollarSign, MessageSquare, LogOut, User, HelpCircle, FileText, BarChart3, Bell, Send, Star } from 'lucide-react';
import { Image } from 'react-bootstrap';
import { 
  AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import '../assets/css/admin-portal.css';

const BRAND_FORM_DEFAULT = {
  name: '',
  email: '',
  contact_name: '',
  phone: '',
  website: '',
  instagram_handle: '',
  description: '',
  send_invite: true,
};

const BRAND_EDIT_DEFAULT = {
  id: null,
  name: '',
  email: '',
  contact_name: '',
  phone: '',
  website: '',
  instagram_handle: '',
  location: '',
  description: '',
  logo: '',
  default_currency: 'INR',
  has_website: false,
  is_active: true,
  status: 'approved',
  onboarding_fee_paid: false,
  onboarding_fee_paid_at: '',
  onboarding_fee_amount: '',
  onboarding_fee_payment_method: '',
};

const PRODUCT_EDIT_DEFAULT = {
  id: null,
  title: '',
  price: '',
  currency: 'USD',
  description: '',
  image: '',
  link: '',
  link_type: 'website',
  brand_account_id: '',
  categories: '',
};

// Helper functions for button hover effects
const addButtonHover = (e) => {
  e.currentTarget.style.background = '#f5f5f5';
  e.currentTarget.style.borderColor = '#d4d4d4';
};

const removeButtonHover = (e) => {
  e.currentTarget.style.background = '#ffffff';
  e.currentTarget.style.borderColor = '#e5e5e5';
};


function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, isAuthenticated, loading, logout, authFetch } = useAdmin();
  const { success, error: showError } = useToast();

  const [activeSection, setActiveSection] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandsWithoutAccounts, setBrandsWithoutAccounts] = useState([]);
  const [brandsView, setBrandsView] = useState('with-accounts'); // 'with-accounts' or 'without-accounts'
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [trendingProducts, setTrendingProducts] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [brandToggleLoading, setBrandToggleLoading] = useState({});
  const [error, setError] = useState('');

  const [showAddBrand, setShowAddBrand] = useState(false);
  const [brandForm, setBrandForm] = useState(BRAND_FORM_DEFAULT);
  const [brandFormError, setBrandFormError] = useState('');
  const [brandFormSuccess, setBrandFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [editingBrand, setEditingBrand] = useState(null);
  const [brandEditForm, setBrandEditForm] = useState(BRAND_EDIT_DEFAULT);
  const [brandEditError, setBrandEditError] = useState('');

  const [editingProduct, setEditingProduct] = useState(null);
  const [productEditForm, setProductEditForm] = useState(PRODUCT_EDIT_DEFAULT);
  const [productEditError, setProductEditError] = useState('');

  // Notification state
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationForm, setNotificationForm] = useState({ brand_id: '', title: '', message: '', priority: 'normal', send_to_all: false });
  const [notificationFormError, setNotificationFormError] = useState('');
  const [notificationFormSuccess, setNotificationFormSuccess] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);

  // Spotlight settings state
  const [spotlightSettings, setSpotlightSettings] = useState({
    spotlight_enabled: true,
    spotlight_brand_names: [],
    spotlight_limit: 3,
    spotlight_days: 7,
    spotlight_title: 'Brand Spotlight',
    spotlight_subtitle: 'Discover emerging brands from Kashmir',
    spotlight_cta_text: 'Explore Brand',
    upcoming_enabled: false,
    upcoming_brand_name: '',
    upcoming_brand_description: '',
    upcoming_brand_logo_url: '',
    upcoming_brand_launch_date: '',
    upcoming_subtitle: 'Be the first to discover this brand when it launches',
  });
  const [spotlightSaving, setSpotlightSaving] = useState(false);
  const [spotlightBrandInput, setSpotlightBrandInput] = useState('');
  const [spotlightMessage, setSpotlightMessage] = useState('');
  const [uploadingSpotlightLogo, setUploadingSpotlightLogo] = useState(false);

  // Brand logo upload state
  const [uploadingBrandLogo, setUploadingBrandLogo] = useState(false);
  const [brandLogoMessage, setBrandLogoMessage] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/app-admin/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch data based on active section
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchData = async () => {
      setLoadingData(true);
      setError('');

      try {
        if (activeSection === 'overview') {
          const [overviewRes, trendingRes] = await Promise.all([
            authFetch('/api/admin-dashboard/overview/'),
            authFetch('/api/admin-dashboard/trending-products/'),
          ]);
          if (overviewRes.ok) {
            const data = await overviewRes.json();
            setOverview(data);
            // Set recent products from top_products in overview
            setRecentProducts(data.top_products || []);
          }
          if (trendingRes.ok) {
            const data = await trendingRes.json();
            setTrendingProducts(data);
          }
        }

        if (activeSection === 'brands') {
          const [brandsRes, brandsWithoutRes] = await Promise.all([
            authFetch('/api/admin-dashboard/brands/'),
            authFetch('/api/admin-dashboard/brands/without-accounts/'),
          ]);
          if (brandsRes.ok) {
            const data = await brandsRes.json();
            setBrands(data.brands || []);
          }
          if (brandsWithoutRes.ok) {
            const data = await brandsWithoutRes.json();
            setBrandsWithoutAccounts(data.brands || []);
          }
        }

        if (activeSection === 'products') {
          const [productsRes, brandsRes] = await Promise.all([
            authFetch('/api/admin-dashboard/products/'),
            brands.length ? Promise.resolve(null) : authFetch('/api/admin-dashboard/brands/'),
          ]);
          if (productsRes.ok) {
            const data = await productsRes.json();
            setProducts(data.products || []);
          }
          if (brandsRes && brandsRes.ok) {
            const data = await brandsRes.json();
            setBrands(data.brands || []);
          }
        }

        if (activeSection === 'users') {
          const usersRes = await authFetch('/api/admin-dashboard/users/');
          if (usersRes.ok) {
            const data = await usersRes.json();
            setUsers(data.users || []);
            setUserStats(data.stats || null);
          }
        }


        if (activeSection === 'revenue') {
          const revenueRes = await authFetch('/api/admin-dashboard/revenue/');
          if (revenueRes.ok) {
            setRevenue(await revenueRes.json());
          }
        }
        if (activeSection === 'analytics') {
          const analyticsRes = await authFetch('/api/admin-dashboard/analytics/');
          if (analyticsRes.ok) {
            const data = await analyticsRes.json();
            setPlatformAnalytics(data);
          } else {
            setError('Failed to load analytics data');
          }
        }
        if (activeSection === 'notifications') {
          const [notifRes, brandsRes] = await Promise.all([
            authFetch('/api/admin-dashboard/notifications/'),
            brands.length ? Promise.resolve(null) : authFetch('/api/admin-dashboard/brands/'),
          ]);
          if (notifRes.ok) {
            setAdminNotifications(await notifRes.json());
          }
          if (brandsRes && brandsRes.ok) {
            const data = await brandsRes.json();
            setBrands(data.brands || []);
          }
        }
        if (activeSection === 'spotlight') {
          const [settingsRes, brandsRes] = await Promise.all([
            authFetch('/api/admin-dashboard/spotlight-settings/'),
            brands.length ? Promise.resolve(null) : authFetch('/api/admin-dashboard/brands/'),
          ]);
          if (settingsRes.ok) {
            const data = await settingsRes.json();
            setSpotlightSettings(data);
            setSpotlightBrandInput((data.spotlight_brand_names || []).join(', '));
          }
          if (brandsRes && brandsRes.ok) {
            const data = await brandsRes.json();
            setBrands(data.brands || []);
          }
        }
      } catch (err) {
        setError('Failed to load data');
        console.error('Fetch error:', err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [activeSection, authFetch, brands.length, isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate('/app-admin/login');
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setNotificationFormError('');
    setNotificationFormSuccess('');
    
    if (!notificationForm.title || !notificationForm.message) {
      setNotificationFormError('Title and message are required');
      return;
    }
    if (!notificationForm.send_to_all && !notificationForm.brand_id) {
      setNotificationFormError('Select a brand or send to all');
      return;
    }
    
    setSendingNotification(true);
    try {
      const res = await authFetch('/api/admin-dashboard/notifications/send/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notificationForm),
      });
      const data = await res.json();
      if (res.ok) {
        setNotificationFormSuccess(data.message);
        setNotificationForm({ brand_id: '', title: '', message: '', priority: 'normal', send_to_all: false });
        // Refresh notifications list
        const notifRes = await authFetch('/api/admin-dashboard/notifications/');
        if (notifRes.ok) setAdminNotifications(await notifRes.json());
      } else {
        setNotificationFormError(data.error || 'Failed to send notification');
      }
    } catch (err) {
      setNotificationFormError('Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleSpotlightLogoUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setSpotlightMessage('Invalid image type. Use JPEG, PNG, WebP, or GIF.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setSpotlightMessage('Image too large. Maximum size is 5MB.');
      return;
    }
    
    setUploadingSpotlightLogo(true);
    setSpotlightMessage('');
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await authFetch('/api/brand/upload-image/', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        setSpotlightSettings(prev => ({ ...prev, upcoming_brand_logo_url: data.url }));
        setSpotlightMessage('Logo uploaded successfully!');
        setTimeout(() => setSpotlightMessage(''), 3000);
      } else {
        setSpotlightMessage(data.error || 'Failed to upload logo');
      }
    } catch (err) {
      setSpotlightMessage('Failed to upload logo');
    } finally {
      setUploadingSpotlightLogo(false);
    }
  };

  const handleSpotlightLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleSpotlightLogoUpload(file);
    }
  };

  const handleBrandLogoUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setBrandLogoMessage('Invalid image type. Use JPEG, PNG, WebP, or GIF.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setBrandLogoMessage('Image too large. Maximum size is 5MB.');
      return;
    }
    
    setUploadingBrandLogo(true);
    setBrandLogoMessage('');
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await authFetch('/api/admin/upload-brand-logo/', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        setBrandEditForm(prev => ({ ...prev, logo: data.url }));
        setBrandLogoMessage('Logo uploaded successfully!');
        setTimeout(() => setBrandLogoMessage(''), 3000);
      } else {
        setBrandLogoMessage(data.error || 'Failed to upload logo');
      }
    } catch (err) {
      setBrandLogoMessage('Failed to upload logo');
    } finally {
      setUploadingBrandLogo(false);
    }
  };

  const handleBrandLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleBrandLogoUpload(file);
    }
  };

  const handleSaveSpotlightSettings = async () => {
    setSpotlightSaving(true);
    setSpotlightMessage('');
    try {
      const brandNames = spotlightBrandInput
        .split(',')
        .map(n => n.trim())
        .filter(Boolean);

      const res = await authFetch('/api/admin-dashboard/spotlight-settings/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spotlight_enabled: spotlightSettings.spotlight_enabled,
          spotlight_brand_names: brandNames,
          spotlight_limit: spotlightSettings.spotlight_limit,
          spotlight_days: spotlightSettings.spotlight_days,
          spotlight_title: spotlightSettings.spotlight_title,
          spotlight_subtitle: spotlightSettings.spotlight_subtitle,
          spotlight_cta_text: spotlightSettings.spotlight_cta_text,
          upcoming_enabled: spotlightSettings.upcoming_enabled,
          upcoming_brand_name: spotlightSettings.upcoming_brand_name,
          upcoming_brand_description: spotlightSettings.upcoming_brand_description,
          upcoming_brand_logo_url: spotlightSettings.upcoming_brand_logo_url,
          upcoming_brand_launch_date: spotlightSettings.upcoming_brand_launch_date || null,
          upcoming_subtitle: spotlightSettings.upcoming_subtitle,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSpotlightSettings(data);
        setSpotlightBrandInput((data.spotlight_brand_names || []).join(', '));
        setSpotlightMessage('Spotlight settings saved successfully.');
        success('Spotlight settings updated');
      } else {
        setSpotlightMessage(data.error || 'Failed to save settings');
        showError(data.error || 'Failed to save spotlight settings');
      }
    } catch (err) {
      setSpotlightMessage('Failed to save settings');
      showError('Failed to save spotlight settings');
    } finally {
      setSpotlightSaving(false);
    }
  };

  const handleBrandFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBrandForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleBrandEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBrandEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProductEditChange = (e) => {
    const { name, value } = e.target;
    setProductEditForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    setBrandFormError('');
    setBrandFormSuccess('');
    setSubmitting(true);

    try {
      const response = await authFetch('/api/admin-dashboard/brands/', {
        method: 'POST',
        body: JSON.stringify(brandForm),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBrandFormSuccess(data.message + (data.invite_link ? ` Invite link: ${data.invite_link}` : ''));
        setBrandForm(BRAND_FORM_DEFAULT);
        setShowAddBrand(false);
        await refreshBrands();
      } else {
        setBrandFormError(data.error || 'Failed to create brand');
      }
    } catch (err) {
      setBrandFormError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const refreshBrands = async () => {
    const brandsRes = await authFetch('/api/admin-dashboard/brands/');
    if (brandsRes.ok) {
      const data = await brandsRes.json();
      setBrands(data.brands || []);
    }
  };

  const refreshProducts = async () => {
    const productsRes = await authFetch('/api/admin-dashboard/products/');
    if (productsRes.ok) {
      const data = await productsRes.json();
      setProducts(data.products || []);
    }
  };

  const handleResendInvite = async (brandId) => {
    try {
      const response = await authFetch(`/api/admin-dashboard/brands/${brandId}/resend-invite/`, {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`Invite resent! ${data.email_sent ? 'Email sent successfully.' : 'Use this link: ' + data.invite_link}`);
      } else {
        alert(data.error || 'Failed to resend invite');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
  };

  const handleToggleBrandStatus = async (brandId, currentStatus) => {
    try {
      console.log('=== TOGGLING BRAND STATUS ===');
      console.log('Brand ID:', brandId);
      console.log('Current Status:', currentStatus);
      console.log('New is_active:', !currentStatus);
      console.log('New status:', currentStatus ? 'suspended' : 'active');
      
      // Set loading state for this specific brand
      setBrandToggleLoading(prev => ({ ...prev, [brandId]: true }));
      
      const requestBody = {
        is_active: !currentStatus,
        status: currentStatus ? 'suspended' : 'active',
      };
      
      console.log('Request body:', requestBody);
      console.log('Request URL:', `/api/admin-dashboard/brands/${brandId}/`);
      
      const response = await authFetch(`/api/admin-dashboard/brands/${brandId}/`, {
        method: 'PATCH',
        body: JSON.stringify(requestBody),
      });

      console.log('=== RESPONSE RECEIVED ===');
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        await refreshBrands();
        // Show success message
        const action = !currentStatus ? 'activated' : 'deactivated';
        if (!currentStatus) {
          success(`Brand "${data.brand_name || brandId}" activated successfully!`);
        } else {
          success(`Brand "${data.brand_name || brandId}" deactivated. Email notification sent to brand contact.`);
        }
      } else {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        
        console.error('Parsed error response:', errorData);
        showError(`Failed to update brand status: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('=== NETWORK ERROR ===');
      console.error('Error:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      showError('Failed to update brand status: Network error');
    } finally {
      // Clear loading state for this specific brand
      setBrandToggleLoading(prev => ({ ...prev, [brandId]: false }));
      console.log('=== TOGGLE COMPLETE ===');
    }
  };

  const startEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandEditError('');
    setBrandEditForm({
      id: brand.id,
      name: brand.name || '',
      email: brand.email || '',
      contact_name: brand.contact_name || '',
      phone: brand.phone || '',
      website: brand.website || '',
      instagram_handle: brand.instagram_handle || '',
      location: brand.location || '',
      description: brand.description || '',
      logo: brand.logo || '',
      default_currency: brand.default_currency || 'INR',
      has_website: !!brand.has_website,
      is_active: !!brand.is_active,
      status: brand.status || 'approved',
      onboarding_fee_paid: !!brand.onboarding_fee_paid,
      onboarding_fee_paid_at: brand.onboarding_fee_paid_at ? new Date(brand.onboarding_fee_paid_at).toISOString().slice(0, 16) : '',
      onboarding_fee_amount: brand.onboarding_fee_amount || '',
      onboarding_fee_payment_method: brand.onboarding_fee_payment_method || '',
    });
  };

  const saveBrandEdits = async (e) => {
    e.preventDefault();
    setBrandEditError('');

    try {
      const response = await authFetch(`/api/admin-dashboard/brands/${brandEditForm.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: brandEditForm.name,
          email: brandEditForm.email,
          contact_name: brandEditForm.contact_name,
          phone: brandEditForm.phone,
          website: brandEditForm.website,
          instagram_handle: brandEditForm.instagram_handle,
          location: brandEditForm.location,
          description: brandEditForm.description,
          logo: brandEditForm.logo,
          default_currency: brandEditForm.default_currency,
          has_website: brandEditForm.has_website,
          is_active: brandEditForm.is_active,
          status: brandEditForm.status,
          onboarding_fee_paid: brandEditForm.onboarding_fee_paid,
          onboarding_fee_paid_at: brandEditForm.onboarding_fee_paid_at || null,
          onboarding_fee_amount: brandEditForm.onboarding_fee_amount || '',
          onboarding_fee_payment_method: brandEditForm.onboarding_fee_payment_method || '',
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setEditingBrand(null);
        await refreshBrands();
      } else {
        setBrandEditError(data.error || 'Failed to update brand');
      }
    } catch (err) {
      setBrandEditError('Network error. Please try again.');
    }
  };

  const startEditProduct = (product) => {
    setEditingProduct(product);
    setProductEditError('');
    setProductEditForm({
      id: product.id,
      title: product.title || '',
      price: product.price || '',
      currency: product.currency || 'USD',
      description: product.description || '',
      image: product.image || '',
      link: product.link || '',
      link_type: product.link_type || 'website',
      brand_account_id: product.brand_account?.id ? String(product.brand_account.id) : '',
      categories: (product.categories || []).join(', '),
    });
  };

  const saveProductEdits = async (e) => {
    e.preventDefault();
    setProductEditError('');

    try {
      const response = await authFetch('/api/admin-dashboard/products/', {
        method: 'PATCH',
        body: JSON.stringify({
          product_id: productEditForm.id,
          title: productEditForm.title,
          price: productEditForm.price,
          currency: productEditForm.currency,
          description: productEditForm.description,
          image: productEditForm.image,
          link: productEditForm.link,
          link_type: productEditForm.link_type,
          brand_account_id: productEditForm.brand_account_id || null,
          categories: productEditForm.categories
            ? productEditForm.categories.split(',').map(item => item.trim()).filter(Boolean)
            : [],
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setEditingProduct(null);
        await refreshProducts();
      } else {
        setProductEditError(data.error || 'Failed to update product');
      }
    } catch (err) {
      setProductEditError('Network error. Please try again.');
    }
  };

  if (loading || (loadingData && !overview && activeSection === 'overview')) {
    return (
      <div className="admin-portal">
        <div className="admin-loading">
          <div className="admin-spinner" style={{ width: 40, height: 40 }} />
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'brands', label: 'Brands', icon: Package },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'analytics', label: 'Platform Analytics', icon: BarChart3 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'spotlight', label: 'Spotlight', icon: Star },
  ];

  return (
    <div style={styles.layout}>
      {/* Sidebar */}
      <aside className="admin-dark-sidebar" style={styles.sidebar}>
        {/* Admin Header */}
        <div style={styles.sidebarHeader}>
          <div style={styles.logoContainer}>
            <Image 
              src={require('../assets/images/newlogo.png')} 
              alt="Yemberzal"
              style={styles.logoImage}
            />
          </div>
          <h2 style={styles.adminLabel}>ADMIN PORTAL</h2>
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {navItems.map(({ id, label, icon: Icon }) => (
          <button
              key={id}
              className={activeSection === id ? 'admin-nav-active' : ''}
              onClick={() => setActiveSection(id)}
              style={{
                ...styles.navItem,
                ...(activeSection === id ? styles.navItemActive : {})
              }}
              onMouseEnter={(e) => {
                if (activeSection !== id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== id) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
          >
              <Icon style={{ width: '20px', height: '20px' }} />
              <span>{label}</span>
          </button>
          ))}
        </nav>

        {/* Footer Section */}
        <div style={styles.sidebarFooter}>
          <div style={styles.accountSection}>
            <button style={styles.accountLink}>
              <HelpCircle style={{ width: '16px', height: '16px' }} />
              <span>Help & Support</span>
          </button>
            <button style={styles.accountLink}>
              <FileText style={{ width: '16px', height: '16px' }} />
              <span>Documentation</span>
          </button>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              <LogOut style={{ width: '16px', height: '16px' }} />
              <span>Logout</span>
          </button>
        </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div style={styles.mainWrapper}>
        <header style={styles.topbar}>
          <div>
            <h1 style={styles.topbarTitle}>
              {activeSection === 'overview' && 'Overview'}
              {activeSection === 'brands' && 'Brands'}
              {activeSection === 'products' && 'Products'}
              {activeSection === 'revenue' && 'Revenue'}
              {activeSection === 'users' && 'Users'}
              {activeSection === 'analytics' && 'Platform Analytics'}
              {activeSection === 'notifications' && 'Notifications'}
              {activeSection === 'spotlight' && 'Brand Spotlight'}
            </h1>
          </div>
          <div style={styles.topbarUser}>
            <span style={styles.userLabel}>ADMIN</span>
            <div style={styles.userAvatar}>{admin?.email?.charAt(0).toUpperCase() || 'A'}</div>
          </div>
        </header>

        <main style={styles.main}>
          {error && (
            <div className="admin-alert admin-alert-error" style={{ marginBottom: 24 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {activeSection === 'overview' && overview && (
            <>
              {/* Primary Stats Grid */}
              <div style={overviewStyles.statsGrid}>
                <div style={overviewStyles.statCard}>
                  <div style={overviewStyles.statHeader}>
                    <div style={overviewStyles.statLabel}>TOTAL BRANDS</div>
                  </div>
                  <div style={overviewStyles.statValue}>{overview.overview?.brands?.total || 0}</div>
                  <div style={overviewStyles.statDescription}>
                    {overview.overview?.brands?.active || 0} active • {overview.overview?.brands?.new_this_month || 0} new this month
                  </div>
                </div>
                <div style={overviewStyles.statCard}>
                  <div style={overviewStyles.statHeader}>
                    <div style={overviewStyles.statLabel}>TOTAL PRODUCTS</div>
                  </div>
                  <div style={overviewStyles.statValue}>{overview.overview?.products?.total || 0}</div>
                  <div style={overviewStyles.statDescription}>
                    {overview.overview?.products?.manual || 0} manual • {overview.overview?.products?.scraped || 0} synced
                  </div>
                </div>
                <div style={overviewStyles.statCard}>
                  <div style={overviewStyles.statHeader}>
                    <div style={overviewStyles.statLabel}>TOTAL USERS</div>
                  </div>
                  <div style={overviewStyles.statValue}>{overview.overview?.users?.total || 0}</div>
                  <div style={overviewStyles.statDescription}>
                    {overview.overview?.users?.active_this_week || 0} active this week
                  </div>
                </div>
                <div style={overviewStyles.statCard}>
                  <div style={overviewStyles.statHeader}>
                    <div style={overviewStyles.statLabel}>REVENUE</div>
                  </div>
                  <div style={overviewStyles.statValue}>${(overview.overview?.revenue?.total || 0).toFixed(0)}</div>
                  <div style={overviewStyles.statDescription}>
                    ${(overview.overview?.revenue?.this_month || 0).toFixed(0)} this month
                  </div>
                  <div style={{ 
                    marginTop: '12px', 
                    paddingTop: '12px', 
                    borderTop: '1px solid #e5e5e5',
                    fontSize: '11px',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span>Stripe:</span>
                      <span>${(overview.overview?.revenue?.stripe_revenue || 0).toFixed(0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Onboarding:</span>
                      <span>${(overview.overview?.revenue?.onboarding_revenue || 0).toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Engagement Stats Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '32px' }}>
                <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #171717', minHeight: 'auto', padding: '20px' }}>
                  <div style={overviewStyles.statLabel}>PAGE VIEWS</div>
                  <div style={{ fontSize: '28px', fontWeight: '400', color: '#171717', margin: '8px 0 4px' }}>
                    {(overview.overview?.engagement?.total_page_views || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>Last 30 days</div>
                </div>
                <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #52525b', minHeight: 'auto', padding: '20px' }}>
                  <div style={overviewStyles.statLabel}>PRODUCT VIEWS</div>
                  <div style={{ fontSize: '28px', fontWeight: '400', color: '#171717', margin: '8px 0 4px' }}>
                    {(overview.overview?.engagement?.product_page_views || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>Product detail pages</div>
                </div>
                <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #a1a1aa', minHeight: 'auto', padding: '20px' }}>
                  <div style={overviewStyles.statLabel}>BRAND PAGE VIEWS</div>
                  <div style={{ fontSize: '28px', fontWeight: '400', color: '#171717', margin: '8px 0 4px' }}>
                    {(overview.overview?.engagement?.brand_page_views || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>Brand profile pages</div>
                </div>
                <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #ef4444', minHeight: 'auto', padding: '20px' }}>
                  <div style={overviewStyles.statLabel}>PRODUCT SAVES</div>
                  <div style={{ fontSize: '28px', fontWeight: '400', color: '#171717', margin: '8px 0 4px' }}>
                    {(overview.overview?.engagement?.product_saves || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>User wishlists</div>
                </div>
                <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #f59e0b', minHeight: 'auto', padding: '20px' }}>
                  <div style={overviewStyles.statLabel}>BRAND FOLLOWS</div>
                  <div style={{ fontSize: '28px', fontWeight: '400', color: '#171717', margin: '8px 0 4px' }}>
                    {(overview.overview?.engagement?.brand_saves || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>Brand bookmarks</div>
                </div>
              </div>

              {/* Daily Traffic Chart */}
              <div style={overviewStyles.chartCard}>
                <div style={overviewStyles.chartHeader}>
                  <div>
                    <div style={overviewStyles.sectionLabel}>T R A F F I C</div>
                    <h3 style={overviewStyles.chartTitle}>Sessions & Page Views</h3>
                  </div>
                  <span style={overviewStyles.chartPeriod}>Last 30 days</span>
                </div>
                <div style={{ height: '280px', marginTop: '16px' }}>
                  {overview.daily_visitors?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={overview.daily_visitors} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#171717" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#171717" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="pvGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#71717a" stopOpacity={0.15}/>
                            <stop offset="95%" stopColor="#71717a" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#a1a1aa' }} 
                          axisLine={{ stroke: '#e5e5e5' }}
                          tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: '#a1a1aa' }} 
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '13px',
                          }}
                          labelStyle={{ fontWeight: '600', marginBottom: '4px' }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="visitors"
                          name="Sessions"
                          stroke="#171717"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#visitorGradient)"
                          dot={false}
                          activeDot={{ r: 4, fill: '#171717', stroke: '#ffffff', strokeWidth: 2 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="pageViews"
                          name="Page Views"
                          stroke="#71717a"
                          strokeWidth={1.5}
                          fillOpacity={1}
                          fill="url(#pvGradient)"
                          dot={false}
                          activeDot={{ r: 3, fill: '#71717a', stroke: '#ffffff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={overviewStyles.emptyChart}>
                      <p style={overviewStyles.emptyText}>No visitor data yet.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Three Column Grid: Top Brands by Views, Likes, Follows */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
                {/* Top Brands by Views */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>M O S T &nbsp; V I E W E D</div>
                    <h3 style={overviewStyles.cardTitle}>Top by Views</h3>
                  </div>
                  <div style={overviewStyles.list}>
                    {(overview.top_brands_by_views || []).map((brand, idx) => (
                      <div key={`views-${brand.name}-${idx}`} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0',
                        borderBottom: idx < (overview.top_brands_by_views?.length || 0) - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#a1a1aa', width: '18px', flexShrink: 0 }}>{idx + 1}</span>
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '8px', background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', color: '#171717', overflow: 'hidden', flexShrink: 0,
                          }}>
                            {brand.logo ? (
                              <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : brand.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand.name}</p>
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a1a1aa' }}>{brand.product_count} products</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#171717', flexShrink: 0 }} title="Views">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{brand.views || 0}</span>
                        </div>
                      </div>
                    ))}
                    {(!overview.top_brands_by_views || overview.top_brands_by_views.length === 0) && (
                      <div style={overviewStyles.emptyState}><p style={overviewStyles.emptyText}>No data yet.</p></div>
                    )}
                  </div>
                </div>

                {/* Top Brands by Likes (Saves) */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>M O S T &nbsp; L I K E D</div>
                    <h3 style={overviewStyles.cardTitle}>Top by Likes</h3>
                  </div>
                  <div style={overviewStyles.list}>
                    {(overview.top_brands_by_likes || []).map((brand, idx) => (
                      <div key={`likes-${brand.name}-${idx}`} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0',
                        borderBottom: idx < (overview.top_brands_by_likes?.length || 0) - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#a1a1aa', width: '18px', flexShrink: 0 }}>{idx + 1}</span>
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '8px', background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', color: '#171717', overflow: 'hidden', flexShrink: 0,
                          }}>
                            {brand.logo ? (
                              <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : brand.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand.name}</p>
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a1a1aa' }}>{brand.product_count} products</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', flexShrink: 0 }} title="Likes">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{brand.saves || 0}</span>
                        </div>
                      </div>
                    ))}
                    {(!overview.top_brands_by_likes || overview.top_brands_by_likes.length === 0) && (
                      <div style={overviewStyles.emptyState}><p style={overviewStyles.emptyText}>No data yet.</p></div>
                    )}
                  </div>
                </div>

                {/* Top Brands by Follows (Trending) */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>T R E N D I N G</div>
                    <h3 style={overviewStyles.cardTitle}>Top by Follows</h3>
                  </div>
                  <div style={overviewStyles.list}>
                    {(overview.top_brands_by_follows || []).map((brand, idx) => (
                      <div key={`follows-${brand.name}-${idx}`} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0',
                        borderBottom: idx < (overview.top_brands_by_follows?.length || 0) - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#a1a1aa', width: '18px', flexShrink: 0 }}>{idx + 1}</span>
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '8px', background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', color: '#171717', overflow: 'hidden', flexShrink: 0,
                          }}>
                            {brand.logo ? (
                              <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : brand.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand.name}</p>
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a1a1aa' }}>{brand.product_count} products</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8b5cf6', flexShrink: 0 }} title="Follows">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <line x1="19" y1="8" x2="19" y2="14"/>
                            <line x1="22" y1="11" x2="16" y2="11"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{brand.follows || 0}</span>
                        </div>
                      </div>
                    ))}
                    {(!overview.top_brands_by_follows || overview.top_brands_by_follows.length === 0) && (
                      <div style={overviewStyles.emptyState}><p style={overviewStyles.emptyText}>No data yet.</p></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Two Column Grid: Trending Products by Views and Saves */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '24px' }}>
                {/* Top Products by Views */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>T R E N D I N G &nbsp; B Y &nbsp; V I E W S</div>
                    <h3 style={overviewStyles.cardTitle}>Most Viewed Products</h3>
                    <span style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: '400' }}>{trendingProducts?.period || 'Last 30 days'}</span>
                  </div>
                  <div style={overviewStyles.list}>
                    {(trendingProducts?.top_products_by_views || []).map((product, idx) => (
                      <div key={`views-${product.id}-${idx}`} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0',
                        borderBottom: idx < (trendingProducts?.top_products_by_views?.length || 0) - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#a1a1aa', width: '18px', flexShrink: 0 }}>{idx + 1}</span>
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '8px', background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', color: '#171717', overflow: 'hidden', flexShrink: 0,
                          }}>
                            {product.image ? (
                              <img src={product.image} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ fontSize: '10px', textAlign: 'center', padding: '2px' }}>No Img</div>
                            )}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.title}</p>
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a1a1aa' }}>{product.brand}</p>
                            {product.price && (
                              <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#059669', fontWeight: '500' }}>
                                {product.currency === 'INR' ? '₹' : product.currency === 'USD' ? '$' : product.currency}{product.price}
                              </p>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#171717', flexShrink: 0 }} title="Views">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{product.views || 0}</span>
                        </div>
                      </div>
                    ))}
                    {(!trendingProducts?.top_products_by_views || trendingProducts.top_products_by_views.length === 0) && (
                      <div style={overviewStyles.emptyState}><p style={overviewStyles.emptyText}>No view data yet.</p></div>
                    )}
                  </div>
                </div>

                {/* Top Products by Saves */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>T R E N D I N G &nbsp; B Y &nbsp; S A V E S</div>
                    <h3 style={overviewStyles.cardTitle}>Most Saved Products</h3>
                    <span style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: '400' }}>{trendingProducts?.period || 'Last 30 days'}</span>
                  </div>
                  <div style={overviewStyles.list}>
                    {(trendingProducts?.top_products_by_saves || []).map((product, idx) => (
                      <div key={`saves-${product.id}-${idx}`} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0',
                        borderBottom: idx < (trendingProducts?.top_products_by_saves?.length || 0) - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#a1a1aa', width: '18px', flexShrink: 0 }}>{idx + 1}</span>
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '8px', background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: '600', color: '#171717', overflow: 'hidden', flexShrink: 0,
                          }}>
                            {product.image ? (
                              <img src={product.image} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ fontSize: '10px', textAlign: 'center', padding: '2px' }}>No Img</div>
                            )}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.title}</p>
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a1a1aa' }}>{product.brand}</p>
                            {product.price && (
                              <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#059669', fontWeight: '500' }}>
                                {product.currency === 'INR' ? '₹' : product.currency === 'USD' ? '$' : product.currency}{product.price}
                              </p>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', flexShrink: 0 }} title="Saves">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{product.saves || 0}</span>
                        </div>
                      </div>
                    ))}
                    {(!trendingProducts?.top_products_by_saves || trendingProducts.top_products_by_saves.length === 0) && (
                      <div style={overviewStyles.emptyState}><p style={overviewStyles.emptyText}>No save data yet.</p></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Products */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '24px' }}>

                {/* Top Products Carousel */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>T R E N D I N G</div>
                    <h3 style={overviewStyles.cardTitle}>Top Products</h3>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '3px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}>
                    {(overview.top_products || recentProducts)?.slice(0, 8).map((product, idx) => (
                      <div 
                        key={product.id || idx}
                        style={{
                          position: 'relative',
                          paddingBottom: '130%',
                          background: '#f5f5f5',
                          overflow: 'hidden',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          const img = e.currentTarget.querySelector('img');
                          const overlay = e.currentTarget.querySelector('.prod-overlay');
                          if (img) img.style.transform = 'scale(1.08)';
                          if (overlay) overlay.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const img = e.currentTarget.querySelector('img');
                          const overlay = e.currentTarget.querySelector('.prod-overlay');
                          if (img) img.style.transform = 'scale(1)';
                          if (overlay) overlay.style.opacity = '0';
                        }}
                      >
                        <img
                          src={product.image}
                          alt=""
                          style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                            objectFit: 'cover', transition: 'transform 0.4s ease',
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default-image.avif';
                          }}
                        />
                        <div
                          className="prod-overlay"
                          style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 8px 8px',
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                            opacity: 0, transition: 'opacity 0.3s ease',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ffffff', fontSize: '11px' }}>
                            {product.views > 0 && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                </svg>
                                {product.views}
                              </span>
                            )}
                            {product.saves > 0 && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                                {product.saves}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(!overview.top_products || overview.top_products.length === 0) && recentProducts.length === 0 && (
                    <div style={overviewStyles.emptyState}>
                      <p style={overviewStyles.emptyText}>No products added yet.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Growth Charts: Brand + Product growth over time */}
              <div style={overviewStyles.twoColumnGrid}>
                <div style={overviewStyles.chartCard}>
                  <div style={overviewStyles.chartHeader}>
                    <div>
                      <div style={overviewStyles.sectionLabel}>G R O W T H</div>
                      <h3 style={overviewStyles.chartTitle}>Brand Growth</h3>
                    </div>
                    <span style={overviewStyles.chartPeriod}>Last 6 months</span>
                  </div>
                  <div style={{ height: '220px' }}>
                    {overview.brand_growth?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={overview.brand_growth}>
                          <defs>
                            <linearGradient id="brandGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#171717" stopOpacity={0.15}/>
                              <stop offset="95%" stopColor="#171717" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a1a1aa' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px' }} />
                          <Area type="monotone" dataKey="brands" stroke="#171717" strokeWidth={2} fill="url(#brandGrowthGrad)" dot={{ r: 3, fill: '#171717' }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={overviewStyles.emptyChart}><p style={overviewStyles.emptyText}>No growth data yet.</p></div>
                    )}
                  </div>
                </div>

                <div style={overviewStyles.chartCard}>
                  <div style={overviewStyles.chartHeader}>
                    <div>
                      <div style={overviewStyles.sectionLabel}>G R O W T H</div>
                      <h3 style={overviewStyles.chartTitle}>Product Catalog Growth</h3>
                    </div>
                    <span style={overviewStyles.chartPeriod}>Last 6 months</span>
                  </div>
                  <div style={{ height: '220px' }}>
                    {overview.product_growth?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={overview.product_growth}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a1a1aa' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px' }} />
                          <Bar dataKey="products" fill="#171717" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={overviewStyles.emptyChart}><p style={overviewStyles.emptyText}>No growth data yet.</p></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Distribution + Try-On & Revenue Summary */}
              <div style={overviewStyles.twoColumnGrid}>
                {/* Category Distribution Pie Chart */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>C A T E G O R I E S</div>
                    <h3 style={overviewStyles.cardTitle}>Product Categories</h3>
                  </div>
                  {(overview.category_distribution || []).length > 0 ? (
                    <div style={{ height: '280px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={overview.category_distribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, count }) => `${name}: ${count}`}
                            outerRadius={100}
                            dataKey="count"
                          >
                            {(overview.category_distribution || []).map((entry, index) => (
                              <Cell key={`cat-${index}`} fill={['#171717', '#3f3f46', '#52525b', '#71717a', '#a1a1aa', '#d4d4d4', '#e5e5e5', '#f5f5f5', '#404040', '#737373'][index % 10]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div style={overviewStyles.emptyState}>
                      <p style={overviewStyles.emptyText}>No category data available.</p>
                    </div>
                  )}
                </div>

                {/* Platform Health Summary */}
                <div style={overviewStyles.card}>
                  <div style={overviewStyles.cardHeader}>
                    <div style={overviewStyles.sectionLabel}>P L A T F O R M</div>
                    <h3 style={overviewStyles.cardTitle}>Health Summary</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fafafa', borderRadius: '10px' }}>
                      <span style={{ fontSize: '14px', color: '#171717', fontWeight: '500' }}>Try-On Uses</span>
                      <span style={{ fontSize: '20px', fontWeight: '400', color: '#171717' }}>{(overview.overview?.tryon?.total_uses || 0).toLocaleString()}</span>
                    </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fafafa', borderRadius: '10px' }}>
                      <span style={{ fontSize: '14px', color: '#171717', fontWeight: '500' }}>Credits Sold</span>
                      <span style={{ fontSize: '20px', fontWeight: '400', color: '#171717' }}>{overview.overview?.revenue?.credits_sold || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fafafa', borderRadius: '10px' }}>
                      <span style={{ fontSize: '14px', color: '#171717', fontWeight: '500' }}>Products This Month</span>
                      <span style={{ fontSize: '20px', fontWeight: '400', color: '#171717' }}>{overview.overview?.products?.new_this_month || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fafafa', borderRadius: '10px' }}>
                      <span style={{ fontSize: '14px', color: '#171717', fontWeight: '500' }}>Pending Brands</span>
                      <span style={{ fontSize: '20px', fontWeight: '400', color: overview.overview?.brands?.pending > 0 ? '#f59e0b' : '#171717' }}>{overview.overview?.brands?.pending || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeSection === 'brands' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>B R A N D S</div>
                  <h2 style={sectionStyles.title}>Brand Management</h2>
                  <p style={sectionStyles.subtitle}>All brands and owner accounts</p>
                </div>
                <button
                  style={sectionStyles.primaryButton}
                  onClick={() => setShowAddBrand(!showAddBrand)}
                >
                  Add Brand Profile
                </button>
              </div>

              {/* View Toggle */}
              <div style={sectionStyles.tabs}>
                <button
                  style={{
                    ...sectionStyles.tab,
                    ...(brandsView === 'with-accounts' ? sectionStyles.tabActive : {})
                  }}
                  onClick={() => setBrandsView('with-accounts')}
                >
                  Brand Accounts ({brands.length})
                </button>
                <button
                  style={{
                    ...sectionStyles.tab,
                    ...(brandsView === 'without-accounts' ? sectionStyles.tabActive : {})
                  }}
                  onClick={() => setBrandsView('without-accounts')}
                >
                  Brands Without Accounts ({brandsWithoutAccounts.length})
                </button>
              </div>

              {showAddBrand && (
                <div style={formStyles.card}>
                  <div style={formStyles.cardHeader}>
                    <h3 style={formStyles.cardTitle}>Add New Brand Owner</h3>
                  </div>
                  {brandFormError && <div className="admin-alert admin-alert-error">{brandFormError}</div>}
                  {brandFormSuccess && <div className="admin-alert admin-alert-success">{brandFormSuccess}</div>}
                  <form onSubmit={handleAddBrand}>
                    <div className="admin-form-grid">
                      <div className="admin-form-group">
                        <label className="admin-form-label">Brand Name *</label>
                        <input
                          type="text"
                          name="name"
                          className="admin-form-input"
                          value={brandForm.name}
                          onChange={handleBrandFormChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Owner Email *</label>
                        <input
                          type="email"
                          name="email"
                          className="admin-form-input"
                          value={brandForm.email}
                          onChange={handleBrandFormChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Contact Name *</label>
                        <input
                          type="text"
                          name="contact_name"
                          className="admin-form-input"
                          value={brandForm.contact_name}
                          onChange={handleBrandFormChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Phone</label>
                        <input
                          type="text"
                          name="phone"
                          className="admin-form-input"
                          value={brandForm.phone}
                          onChange={handleBrandFormChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Website</label>
                        <input
                          type="url"
                          name="website"
                          className="admin-form-input"
                          value={brandForm.website}
                          onChange={handleBrandFormChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Instagram</label>
                        <input
                          type="text"
                          name="instagram_handle"
                          className="admin-form-input"
                          value={brandForm.instagram_handle}
                          onChange={handleBrandFormChange}
                        />
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Description</label>
                      <textarea
                        name="description"
                        className="admin-form-textarea"
                        value={brandForm.description}
                        onChange={handleBrandFormChange}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          name="send_invite"
                          checked={brandForm.send_invite}
                          onChange={handleBrandFormChange}
                        />
                        <span>Send invite email to brand owner</span>
                      </label>
                    </div>
                    <div className="admin-form-actions">
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => setShowAddBrand(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting}>
                        {submitting ? 'Creating...' : 'Create Brand'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {editingBrand && (
                <div style={formStyles.card}>
                  <div style={formStyles.cardHeader}>
                    <h3 style={formStyles.cardTitle}>Edit Brand</h3>
                  </div>
                  {brandEditError && <div className="admin-alert admin-alert-error">{brandEditError}</div>}
                  <form onSubmit={saveBrandEdits}>
                    <div className="admin-form-grid">
                      <div className="admin-form-group">
                        <label className="admin-form-label">Brand Name</label>
                        <input
                          type="text"
                          name="name"
                          className="admin-form-input"
                          value={brandEditForm.name}
                          onChange={handleBrandEditChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="admin-form-input"
                          value={brandEditForm.email}
                          onChange={handleBrandEditChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Contact Name</label>
                        <input
                          type="text"
                          name="contact_name"
                          className="admin-form-input"
                          value={brandEditForm.contact_name}
                          onChange={handleBrandEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Phone</label>
                        <input
                          type="text"
                          name="phone"
                          className="admin-form-input"
                          value={brandEditForm.phone}
                          onChange={handleBrandEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Website</label>
                        <input
                          type="url"
                          name="website"
                          className="admin-form-input"
                          value={brandEditForm.website}
                          onChange={handleBrandEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Instagram</label>
                        <input
                          type="text"
                          name="instagram_handle"
                          className="admin-form-input"
                          value={brandEditForm.instagram_handle}
                          onChange={handleBrandEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Location</label>
                        <input
                          type="text"
                          name="location"
                          className="admin-form-input"
                          value={brandEditForm.location}
                          onChange={handleBrandEditChange}
                          placeholder="e.g. Srinagar, Kashmir"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Brand Logo</label>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleBrandLogoFileSelect}
                              disabled={uploadingBrandLogo}
                              style={{ display: 'none' }}
                              id="brand-logo-upload"
                            />
                            <button
                              type="button"
                              onClick={() => document.getElementById('brand-logo-upload').click()}
                              disabled={uploadingBrandLogo}
                              className="admin-button"
                              style={{
                                background: uploadingBrandLogo ? '#f3f4f6' : '#ffffff',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: uploadingBrandLogo ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {uploadingBrandLogo ? 'Uploading...' : 'Upload Logo'}
                            </button>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              or paste URL below
                            </span>
                          </div>
                          {brandLogoMessage && (
                            <div style={{
                              fontSize: '12px',
                              color: brandLogoMessage.includes('success') ? '#059669' : '#dc2626',
                              marginBottom: '8px'
                            }}>
                              {brandLogoMessage}
                            </div>
                          )}
                          {brandEditForm.logo && (
                            <div style={{ marginBottom: '8px' }}>
                              <img 
                                src={brandEditForm.logo} 
                                alt="Brand logo preview" 
                                style={{ 
                                  maxWidth: '80px', 
                                  maxHeight: '80px', 
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <input
                          type="text"
                          name="logo"
                          className="admin-form-input"
                          value={brandEditForm.logo}
                          onChange={handleBrandEditChange}
                          placeholder="https://example.com/logo.jpg"
                        />
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          Upload an image or enter a logo URL (max 1000 characters)
                        </div>
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Default Currency</label>
                        <select
                          name="default_currency"
                          className="admin-form-input"
                          value={brandEditForm.default_currency}
                          onChange={handleBrandEditChange}
                        >
                          <option value="USD">USD</option>
                          <option value="INR">INR</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Status</label>
                        <select
                          name="status"
                          className="admin-form-input"
                          value={brandEditForm.status}
                          onChange={handleBrandEditChange}
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e5e5' }}>
                      <div style={{ marginBottom: 8, fontWeight: 600, color: '#374151' }}>Onboarding fee</div>
                      <div className="admin-form-grid">
                        <label className="admin-checkbox">
                          <input
                            type="checkbox"
                            name="onboarding_fee_paid"
                            checked={brandEditForm.onboarding_fee_paid}
                            onChange={handleBrandEditChange}
                          />
                          <span>Fee paid</span>
                        </label>
                        <div className="admin-form-group">
                          <label className="admin-form-label">Paid at (optional)</label>
                          <input
                            type="datetime-local"
                            name="onboarding_fee_paid_at"
                            className="admin-form-input"
                            value={brandEditForm.onboarding_fee_paid_at}
                            onChange={handleBrandEditChange}
                          />
                        </div>
                        <div className="admin-form-group">
                          <label className="admin-form-label">Amount (e.g. 5000 INR)</label>
                          <input
                            type="text"
                            name="onboarding_fee_amount"
                            className="admin-form-input"
                            placeholder="5000 INR"
                            value={brandEditForm.onboarding_fee_amount}
                            onChange={handleBrandEditChange}
                          />
                        </div>
                        <div className="admin-form-group">
                          <label className="admin-form-label">Payment method</label>
                          <select
                            name="onboarding_fee_payment_method"
                            className="admin-form-input"
                            value={brandEditForm.onboarding_fee_payment_method}
                            onChange={handleBrandEditChange}
                          >
                            <option value="">—</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="UPI">UPI</option>
                            <option value="Card">Card</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Description</label>
                      <textarea
                        name="description"
                        className="admin-form-textarea"
                        value={brandEditForm.description}
                        onChange={handleBrandEditChange}
                      />
                    </div>
                    <div className="admin-form-grid">
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          name="has_website"
                          checked={brandEditForm.has_website}
                          onChange={handleBrandEditChange}
                        />
                        <span>Has Website</span>
                      </label>
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={brandEditForm.is_active}
                          onChange={handleBrandEditChange}
                        />
                        <span>Active</span>
                      </label>
                    </div>
                    <div className="admin-form-actions">
                      <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setEditingBrand(null)}>
                        Cancel
                      </button>
                      <button type="submit" className="admin-btn admin-btn-primary">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {loadingData ? (
                <div style={sectionStyles.loading}>
                  <div style={sectionStyles.spinner} />
                </div>
              ) : brandsView === 'with-accounts' ? (
                <div style={tableStyles.container}>
                  <table style={tableStyles.table}>
                    <thead style={tableStyles.thead}>
                      <tr>
                        <th style={tableStyles.th}>Brand</th>
                        <th style={tableStyles.th}>Contact</th>
                        <th style={tableStyles.th}>Status</th>
                        <th style={tableStyles.th}>Onboarding</th>
                        <th style={tableStyles.th}>Products</th>
                        <th style={tableStyles.th}>Joined</th>
                        <th style={tableStyles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brands.map(brand => (
                        <tr 
                          key={brand.id}
                          style={tableStyles.tr}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tableStyles.td}>
                            <div style={tableStyles.brandCell}>
                              <div style={tableStyles.brandAvatar}>
                                {brand.logo ? (
                                  <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  brand.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <p style={tableStyles.brandName}>{brand.name}</p>
                                <p style={tableStyles.brandEmail}>{brand.email}</p>
                              </div>
                            </div>
                          </td>
                          <td style={tableStyles.td}>
                            <p style={{ margin: 0, fontWeight: '500' }}>{brand.contact_name}</p>
                            <p style={tableStyles.mutedText}>{brand.phone || '-'}</p>
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{
                              ...tableStyles.statusBadge,
                              ...(brand.status === 'active' || brand.status === 'approved' ? tableStyles.statusBadgeActive : {}),
                              ...(brand.status === 'pending' ? tableStyles.statusBadgePending : {}),
                              ...(brand.status === 'suspended' ? tableStyles.statusBadgeSuspended : {}),
                            }}>
                              {brand.status}
                            </span>
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 500,
                              ...(brand.onboarding_fee_paid ? { background: '#dcfce7', color: '#16a34a' } : { background: '#fef3c7', color: '#b45309' }),
                            }}>
                              {brand.onboarding_fee_paid ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td style={tableStyles.td}>{brand.product_count}</td>
                          <td style={tableStyles.td}>{new Date(brand.created_at).toLocaleDateString()}</td>
                          <td style={tableStyles.td}>
                            <div style={tableStyles.actionBtns}>
                              <button 
                                style={tableStyles.actionBtn} 
                                onClick={() => startEditBrand(brand)}
                                onMouseEnter={addButtonHover}
                                onMouseLeave={removeButtonHover}
                              >
                                Edit
                              </button>
                              {!brand.invite_accepted_at && (
                                <button 
                                  style={tableStyles.actionBtn} 
                                  onClick={() => handleResendInvite(brand.id)}
                                  onMouseEnter={addButtonHover}
                                  onMouseLeave={removeButtonHover}
                                >
                                  Invite
                                </button>
                              )}
                              <button
                                style={{
                                  ...tableStyles.actionBtn,
                                  ...(brand.is_active ? tableStyles.actionBtnDanger : tableStyles.actionBtnSuccess),
                                  opacity: brandToggleLoading[brand.id] ? 0.6 : 1,
                                  cursor: brandToggleLoading[brand.id] ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleToggleBrandStatus(brand.id, brand.is_active)}
                                disabled={brandToggleLoading[brand.id]}
                                onMouseEnter={(e) => {
                                  if (!brandToggleLoading[brand.id]) {
                                    e.currentTarget.style.background = brand.is_active ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#ffffff';
                                }}
                              >
                                {brandToggleLoading[brand.id] ? 'Updating...' : (brand.is_active ? 'Deactivate' : 'Activate')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {brands.length === 0 && (
                        <tr>
                          <td colSpan="6" style={tableStyles.emptyCell}>
                            No brands found yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={tableStyles.container}>
                  <table style={tableStyles.table}>
                    <thead style={tableStyles.thead}>
                      <tr>
                        <th style={tableStyles.th}>Brand Name</th>
                        <th style={tableStyles.th}>Products</th>
                        <th style={tableStyles.th}>First Seen</th>
                        <th style={tableStyles.th}>Last Seen</th>
                        <th style={tableStyles.th}>Sample Products</th>
                        <th style={tableStyles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandsWithoutAccounts.map((brand, idx) => (
                        <tr 
                          key={`${brand.name}-${idx}`}
                          style={tableStyles.tr}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tableStyles.td}>
                            <div style={tableStyles.brandCell}>
                              <div style={tableStyles.brandAvatar}>
                                {brand.logo ? (
                                  <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  brand.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <p style={tableStyles.brandName}>{brand.name}</p>
                                <p style={tableStyles.mutedText}>No account</p>
                              </div>
                            </div>
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{ fontWeight: '600', fontSize: '16px' }}>{brand.product_count}</span>
                          </td>
                          <td style={tableStyles.td}>
                            {brand.first_seen ? new Date(brand.first_seen).toLocaleDateString() : '-'}
                          </td>
                          <td style={tableStyles.td}>
                            {brand.last_seen ? new Date(brand.last_seen).toLocaleDateString() : '-'}
                          </td>
                          <td style={tableStyles.td}>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {brand.sample_products?.slice(0, 3).map(product => (
                                <div key={product.id} style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  background: '#f5f5f5',
                                  border: '1px solid #e5e5e5'
                                }}>
                                  <img 
                                    src={product.image} 
                                    alt={product.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = '/default-image.avif';
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </td>
                          <td style={tableStyles.td}>
                            <button 
                              style={tableStyles.actionBtn} 
                              onClick={() => {
                                setBrandForm({ ...BRAND_FORM_DEFAULT, name: brand.name });
                                setShowAddBrand(true);
                                setBrandsView('with-accounts');
                              }}
                              onMouseEnter={addButtonHover}
                              onMouseLeave={removeButtonHover}
                            >
                              Create Account
                            </button>
                          </td>
                        </tr>
                      ))}
                      {brandsWithoutAccounts.length === 0 && (
                        <tr>
                          <td colSpan="6" style={tableStyles.emptyCell}>
                            No brands without accounts found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeSection === 'products' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>P R O D U C T S</div>
                  <h2 style={sectionStyles.title}>Product Catalog</h2>
                  <p style={sectionStyles.subtitle}>All products across brands</p>
                </div>
              </div>

              {editingProduct && (
                <div style={formStyles.card}>
                  <div style={formStyles.cardHeader}>
                    <h3 style={formStyles.cardTitle}>Edit Product</h3>
                  </div>
                  {productEditError && <div className="admin-alert admin-alert-error">{productEditError}</div>}
                  <form onSubmit={saveProductEdits}>
                    <div className="admin-form-grid">
                      <div className="admin-form-group">
                        <label className="admin-form-label">Title</label>
                        <input
                          type="text"
                          name="title"
                          className="admin-form-input"
                          value={productEditForm.title}
                          onChange={handleProductEditChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Price</label>
                        <input
                          type="number"
                          name="price"
                          className="admin-form-input"
                          value={productEditForm.price}
                          onChange={handleProductEditChange}
                          required
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Currency</label>
                        <select
                          name="currency"
                          className="admin-form-input"
                          value={productEditForm.currency}
                          onChange={handleProductEditChange}
                        >
                          <option value="USD">USD</option>
                          <option value="INR">INR</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Brand</label>
                        <select
                          name="brand_account_id"
                          className="admin-form-input"
                          value={productEditForm.brand_account_id}
                          onChange={handleProductEditChange}
                        >
                          <option value="">No linked brand</option>
                          {brands.map(brand => (
                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Image URL</label>
                        <input
                          type="text"
                          name="image"
                          className="admin-form-input"
                          value={productEditForm.image}
                          onChange={handleProductEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Product Link</label>
                        <input
                          type="text"
                          name="link"
                          className="admin-form-input"
                          value={productEditForm.link}
                          onChange={handleProductEditChange}
                        />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-form-label">Link Type</label>
                        <select
                          name="link_type"
                          className="admin-form-input"
                          value={productEditForm.link_type}
                          onChange={handleProductEditChange}
                        >
                          <option value="website">Website</option>
                          <option value="instagram">Instagram</option>
                        </select>
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Categories</label>
                      <input
                        type="text"
                        name="categories"
                        className="admin-form-input"
                        value={productEditForm.categories}
                        onChange={handleProductEditChange}
                        placeholder="Comma-separated categories"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Description</label>
                      <textarea
                        name="description"
                        className="admin-form-textarea"
                        value={productEditForm.description}
                        onChange={handleProductEditChange}
                      />
                    </div>
                    <div className="admin-form-actions">
                      <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setEditingProduct(null)}>
                        Cancel
                      </button>
                      <button type="submit" className="admin-btn admin-btn-primary">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {loadingData ? (
                <div style={sectionStyles.loading}>
                  <div style={sectionStyles.spinner} />
                </div>
              ) : (
                <div style={tableStyles.container}>
                  <table style={tableStyles.table}>
                    <thead style={tableStyles.thead}>
                      <tr>
                        <th style={tableStyles.th}>Product</th>
                        <th style={tableStyles.th}>Brand</th>
                        <th style={tableStyles.th}>Price</th>
                        <th style={tableStyles.th}>Type</th>
                        <th style={tableStyles.th}>Created</th>
                        <th style={tableStyles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr 
                          key={product.id}
                          style={tableStyles.tr}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tableStyles.td}>
                            <div style={tableStyles.brandCell}>
                              <div style={tableStyles.brandAvatar}>
                                <img
                                  src={product.image}
                                  alt={product.title}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = '/default-image.avif';
                                  }}
                                />
                              </div>
                              <div>
                                <p style={tableStyles.brandName}>{product.title}</p>
                                <p style={tableStyles.brandEmail}>{product.categories?.join(', ') || 'No categories'}</p>
                              </div>
                            </div>
                          </td>
                          <td style={tableStyles.td}>{product.brand_account?.name || product.brand || 'Unknown'}</td>
                          <td style={tableStyles.td}>${parseFloat(product.price).toFixed(2)}</td>
                          <td style={tableStyles.td}>{product.is_manual ? 'Manual' : 'Scraped'}</td>
                          <td style={tableStyles.td}>{new Date(product.created_at).toLocaleDateString()}</td>
                          <td style={tableStyles.td}>
                            <button 
                              style={tableStyles.actionBtn} 
                              onClick={() => startEditProduct(product)}
                              onMouseEnter={addButtonHover}
                              onMouseLeave={removeButtonHover}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {products.length === 0 && (
                        <tr>
                          <td colSpan="6" style={tableStyles.emptyCell}>
                            No products found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeSection === 'users' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>U S E R S</div>
                  <h2 style={sectionStyles.title}>User Insights</h2>
                  <p style={sectionStyles.subtitle}>Real usage, credits, and engagement data from database</p>
                </div>
              </div>

              {/* User Stats */}
              {userStats && (
                <div style={overviewStyles.statsGrid}>
                  <div style={overviewStyles.statCard}>
                    <div style={overviewStyles.statHeader}>
                      <div style={overviewStyles.statLabel}>TOTAL USERS</div>
                    </div>
                    <div style={overviewStyles.statValue}>{userStats.total_registered || 0}</div>
                    <div style={overviewStyles.statDescription}>Registered users</div>
                  </div>
                  <div style={overviewStyles.statCard}>
                    <div style={overviewStyles.statHeader}>
                      <div style={overviewStyles.statLabel}>ACTIVE THIS WEEK</div>
                    </div>
                    <div style={overviewStyles.statValue}>{userStats.active_this_week || 0}</div>
                    <div style={overviewStyles.statDescription}>Last 7 days</div>
                  </div>
                  <div style={overviewStyles.statCard}>
                    <div style={overviewStyles.statHeader}>
                      <div style={overviewStyles.statLabel}>ACTIVE THIS MONTH</div>
                    </div>
                    <div style={overviewStyles.statValue}>{userStats.active_this_month || 0}</div>
                    <div style={overviewStyles.statDescription}>Last 30 days</div>
                  </div>
                  <div style={overviewStyles.statCard}>
                    <div style={overviewStyles.statHeader}>
                      <div style={overviewStyles.statLabel}>TOTAL TRY-ONS</div>
                    </div>
                    <div style={overviewStyles.statValue}>{userStats.total_tryon_uses || 0}</div>
                    <div style={overviewStyles.statDescription}>All time try-on uses</div>
                  </div>
                </div>
              )}

              {loadingData ? (
                <div style={sectionStyles.loading}>
                  <div style={sectionStyles.spinner} />
                </div>
              ) : (
                <div style={tableStyles.container}>
                  <table style={tableStyles.table}>
                    <thead style={tableStyles.thead}>
                      <tr>
                        <th style={tableStyles.th}>User</th>
                        <th style={tableStyles.th}>Try-On Uses</th>
                        <th style={tableStyles.th}>Credits</th>
                        <th style={tableStyles.th}>Saved Products</th>
                        <th style={tableStyles.th}>Joined</th>
                        <th style={tableStyles.th}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, idx) => (
                        <tr 
                          key={idx}
                          style={tableStyles.tr}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tableStyles.td}>
                            <div style={tableStyles.brandCell}>
                              <div style={{
                                ...tableStyles.brandAvatar,
                                background: '#171717',
                                color: '#ffffff',
                                fontSize: '14px',
                              }}>
                                {(user.email || 'A').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>{user.email || 'Anonymous'}</p>
                                <p style={tableStyles.mutedText}>{user.user_id?.slice(0, 8)}...</p>
                              </div>
                            </div>
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{ fontWeight: '600' }}>{user.tryon_uses}</span>
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{
                              display: 'inline-flex',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: user.credit_balance > 0 ? 'rgba(16, 185, 129, 0.1)' : '#f5f5f5',
                              color: user.credit_balance > 0 ? '#10b981' : '#71717a',
                            }}>
                              {user.credit_balance}
                            </span>
                          </td>
                          <td style={tableStyles.td}>{user.saved_products || 0}</td>
                          <td style={tableStyles.td}>{user.joined ? new Date(user.joined).toLocaleDateString() : '-'}</td>
                          <td style={tableStyles.td}>{new Date(user.last_active).toLocaleDateString()}</td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan="7" style={tableStyles.emptyCell}>
                            No user data available yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          
          {activeSection === 'revenue' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>R E V E N U E</div>
                  <h2 style={sectionStyles.title}>Revenue Analytics</h2>
                  <p style={sectionStyles.subtitle}>Revenue summary and transactions</p>
                </div>
              </div>

              {revenue?.totals && (
                <>
                  <div style={overviewStyles.statsGrid}>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statHeader}>
                        <div style={overviewStyles.statLabel}>TOTAL REVENUE</div>
                      </div>
                        <div style={overviewStyles.statValue}>${revenue.totals.revenue.toFixed(2)}</div>
                        <div style={overviewStyles.statDescription}>All time revenue</div>
                      </div>
                      <div style={overviewStyles.statCard}>
                        <div style={overviewStyles.statHeader}>
                          <div style={overviewStyles.statLabel}>TRANSACTIONS</div>
                        </div>
                        <div style={overviewStyles.statValue}>{revenue.totals.transactions}</div>
                        <div style={overviewStyles.statDescription}>Total payments</div>
                      </div>
                      <div style={overviewStyles.statCard}>
                        <div style={overviewStyles.statHeader}>
                          <div style={overviewStyles.statLabel}>CREDITS SOLD</div>
                        </div>
                        <div style={overviewStyles.statValue}>{revenue.totals.credits_sold}</div>
                        <div style={overviewStyles.statDescription}>Credits purchased</div>
                      </div>
                    </div>

                  {/* Revenue Breakdown */}
                  <div style={overviewStyles.statsGrid}>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statHeader}>
                        <div style={overviewStyles.statLabel}>STRIPE REVENUE</div>
                      </div>
                        <div style={overviewStyles.statValue}>${(revenue.totals.stripe_revenue || 0).toFixed(2)}</div>
                        <div style={overviewStyles.statDescription}>From credit purchases</div>
                      </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statHeader}>
                        <div style={overviewStyles.statLabel}>ONBOARDING FEES</div>
                      </div>
                        <div style={overviewStyles.statValue}>${(revenue.totals.onboarding_revenue || 0).toFixed(2)}</div>
                        <div style={overviewStyles.statDescription}>
                          From {revenue.totals.onboarding_count || 0} brands
                        </div>
                      </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statHeader}>
                        <div style={overviewStyles.statLabel}>REVENUE MIX</div>
                      </div>
                        <div style={overviewStyles.statValue}>
                          {revenue.totals.revenue > 0 
                            ? `${((revenue.totals.stripe_revenue || 0) / revenue.totals.revenue * 100).toFixed(1)}% / ${((revenue.totals.onboarding_revenue || 0) / revenue.totals.revenue * 100).toFixed(1)}%`
                            : '0% / 0%'
                          }
                        </div>
                        <div style={overviewStyles.statDescription}>Stripe / Onboarding</div>
                      </div>
                  </div>
                </>
              )}

              {loadingData ? (
                <div style={sectionStyles.loading}>
                  <div style={sectionStyles.spinner} />
                </div>
              ) : (
                <div style={tableStyles.container}>
                  <table style={tableStyles.table}>
                    <thead style={tableStyles.thead}>
                      <tr>
                        <th style={tableStyles.th}>Customer/Brand</th>
                        <th style={tableStyles.th}>Type</th>
                        <th style={tableStyles.th}>Amount</th>
                        <th style={tableStyles.th}>Credits/Payment Method</th>
                        <th style={tableStyles.th}>Status</th>
                        <th style={tableStyles.th}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenue?.payments?.map((payment, idx) => (
                        <tr 
                          key={idx}
                          style={tableStyles.tr}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tableStyles.td}>
                            {payment.type === 'onboarding_fee' 
                              ? payment.brand_name || payment.brand_email || 'Unknown Brand'
                              : payment.user_email || 'Unknown User'
                            }
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: payment.type === 'onboarding_fee' ? '#f0f9ff' : '#f0fdf4',
                              color: payment.type === 'onboarding_fee' ? '#0369a1' : '#166534',
                            }}>
                              {payment.type === 'onboarding_fee' ? 'Onboarding Fee' : 'Credits'}
                            </span>
                          </td>
                          <td style={tableStyles.td}>${payment.amount.toFixed(2)}</td>
                          <td style={tableStyles.td}>
                            {payment.type === 'onboarding_fee' 
                              ? payment.payment_method || 'N/A'
                              : payment.credits || 'N/A'
                            }
                          </td>
                          <td style={tableStyles.td}>
                            <span style={{
                              ...tableStyles.statusBadge,
                              ...(payment.status === 'completed' || payment.status === 'success' ? tableStyles.statusBadgeActive : {}),
                              ...(payment.status === 'pending' ? tableStyles.statusBadgePending : {}),
                              ...(payment.status === 'failed' ? tableStyles.statusBadgeSuspended : {}),
                            }}>
                              {payment.status}
                            </span>
                          </td>
                          <td style={tableStyles.td}>{new Date(payment.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                      {(!revenue?.payments || revenue.payments.length === 0) && (
                        <tr>
                          <td colSpan="6" style={tableStyles.emptyCell}>
                            No payments recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeSection === 'analytics' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>A N A L Y T I C S</div>
                  <h2 style={sectionStyles.title}>Platform Analytics</h2>
                  <p style={sectionStyles.subtitle}>Real-time platform statistics tracked from actual user activity</p>
                </div>
              </div>

              {loadingData ? (
                <div style={sectionStyles.loading}>
                  <div style={sectionStyles.spinner} />
                </div>
              ) : platformAnalytics ? (
                <>
                  {/* ── Key Insight Cards ── */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    {/* Total Reach */}
                    <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #171717' }}>
                      <div style={overviewStyles.statLabel}>TOTAL REACH</div>
                      <div style={overviewStyles.statValue}>
                        {platformAnalytics.reach?.active_users?.toLocaleString() || 0}
                        <span style={{ fontSize: '14px', fontWeight: 400, color: '#71717a', marginLeft: '8px' }}>active users</span>
                      </div>
                      <div style={overviewStyles.statDescription}>
                        {platformAnalytics.reach?.growth_factor > 0
                          ? `~${platformAnalytics.reach.growth_factor}× growth from previous period's baseline`
                          : 'Tracking started — growth data will appear next period'}
                        {platformAnalytics.reach?.previous_period_users > 0 &&
                          ` (prev: ${platformAnalytics.reach.previous_period_users})`}
                      </div>
                    </div>

                    {/* Engagement Quality */}
                    <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #171717' }}>
                      <div style={overviewStyles.statLabel}>ENGAGEMENT QUALITY</div>
                      <div style={overviewStyles.statValue}>
                        {platformAnalytics.engagement?.total_views >= 1000
                          ? `${(platformAnalytics.engagement.total_views / 1000).toFixed(1)}K`
                          : platformAnalytics.engagement?.total_views?.toLocaleString() || 0}
                        <span style={{ fontSize: '14px', fontWeight: 400, color: '#71717a', marginLeft: '8px' }}>total views</span>
                      </div>
                      <div style={overviewStyles.statDescription}>
                        ~{platformAnalytics.engagement?.events_per_user || 0} events per user — users are actively scrolling, clicking, and exploring
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', color: '#a1a1aa', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                          Try-Ons: {platformAnalytics.engagement?.tryon_uses || 0}
                        </span>
                        <span style={{ fontSize: '11px', color: '#a1a1aa', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                          Saves: {platformAnalytics.engagement?.saved_products || 0}
                        </span>
                        <span style={{ fontSize: '11px', color: '#a1a1aa', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                        </span>
                      </div>
                    </div>

                    {/* Mobile-first Audience */}
                    <div style={{ ...overviewStyles.statCard, borderLeft: '4px solid #171717' }}>
                      <div style={overviewStyles.statLabel}>MOBILE-FIRST AUDIENCE</div>
                      <div style={overviewStyles.statValue}>
                        {(() => {
                          const mobile = (platformAnalytics.devices || []).find(d => d.device === 'Mobile');
                          return mobile ? `${mobile.percentage}%` : '—';
                        })()}
                        <span style={{ fontSize: '14px', fontWeight: 400, color: '#71717a', marginLeft: '8px' }}>mobile traffic</span>
                      </div>
                      <div style={overviewStyles.statDescription}>
                        {(platformAnalytics.devices || []).map(d => {
                          const topBrowser = d.top_browsers?.[0]?.name || '';
                          const topOS = d.top_os?.[0]?.name || '';
                          return `${d.device}: ${d.percentage}%${topBrowser ? ` (${topOS}/${topBrowser})` : ''}`;
                        }).join(' · ')}
                      </div>
                    </div>
                  </div>

                  {/* ── Two Column: Top Markets + Traffic Channels ── */}
                  <div style={overviewStyles.twoColumnGrid}>
                    {/* Top Markets */}
                    <div style={overviewStyles.card}>
                      <div style={overviewStyles.cardHeader}>
                        <div style={overviewStyles.sectionLabel}>M A R K E T S</div>
                        <h3 style={overviewStyles.cardTitle}>Top Markets</h3>
                      </div>
                      {(platformAnalytics.top_markets || []).length > 0 ? (
                        <div style={{ marginTop: '16px' }}>
                          {(platformAnalytics.top_markets || []).map((m, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '18px' }}>{{'US':'🇺🇸','IN':'🇮🇳','DE':'🇩🇪','CA':'🇨🇦','GB':'🇬🇧','AU':'🇦🇺','PK':'🇵🇰','AE':'🇦🇪','FR':'🇫🇷','NL':'🇳🇱','SG':'🇸🇬','SA':'🇸🇦','BD':'🇧🇩'}[m.code] || '🌍'}</span>
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>{m.country}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '100px', height: '6px', background: '#f5f5f5', borderRadius: '3px', overflow: 'hidden' }}>
                                  <div style={{ width: `${m.percentage}%`, height: '100%', background: '#171717', borderRadius: '3px' }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '40px', textAlign: 'right' }}>{m.percentage}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#a1a1aa', marginTop: '16px', fontSize: '13px' }}>
                          Country data will populate as page views are tracked.
                        </p>
                      )}
                    </div>

                    {/* Best-Performing Channels */}
                    <div style={overviewStyles.card}>
                      <div style={overviewStyles.cardHeader}>
                        <div style={overviewStyles.sectionLabel}>C H A N N E L S</div>
                        <h3 style={overviewStyles.cardTitle}>Best-Performing Channels</h3>
                      </div>
                      {(platformAnalytics.traffic_channels || []).length > 0 ? (
                        <div style={{ marginTop: '16px' }}>
                          {(platformAnalytics.traffic_channels || []).map((ch, i) => (
                            <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>{ch.source}</span>
                                <span style={{ fontSize: '13px', fontWeight: 600 }}>{ch.percentage}%</span>
                              </div>
                              <div style={{ width: '100%', height: '6px', background: '#f5f5f5', borderRadius: '3px', marginTop: '6px', overflow: 'hidden' }}>
                                <div style={{ width: `${ch.percentage}%`, height: '100%', background: '#171717', borderRadius: '3px' }} />
                              </div>
                              {ch.details && ch.details.length > 0 && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                                  {ch.details.map((d, j) => (
                                    <span key={j} style={{ fontSize: '11px', color: '#71717a', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                                      {d.platform}: {d.percentage}%
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#a1a1aa', marginTop: '16px', fontSize: '13px' }}>
                          Channel data will populate as page views are tracked.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── Core Stats Row ── */}
                  <div style={overviewStyles.statsGrid}>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>SESSIONS</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.sessions?.toLocaleString() || 0}</div>
                      <div style={overviewStyles.statDescription}>Total sessions (30 days)</div>
                    </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>PAGE VIEWS</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.pageViews?.toLocaleString() || 0}</div>
                      <div style={overviewStyles.statDescription}>Total page views</div>
                    </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>BOUNCE RATE</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.bounceRate || 0}%</div>
                      <div style={overviewStyles.statDescription}>Single-page sessions</div>
                    </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>AVG SESSION</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.avgSessionDuration || '0m 0s'}</div>
                      <div style={overviewStyles.statDescription}>Average session duration</div>
                    </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>NEW USERS</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.newUsers?.toLocaleString() || 0}</div>
                      <div style={overviewStyles.statDescription}>
                        {platformAnalytics.overview?.users ? Math.round((platformAnalytics.overview.newUsers / platformAnalytics.overview.users) * 100) : 0}% of total
                      </div>
                    </div>
                    <div style={overviewStyles.statCard}>
                      <div style={overviewStyles.statLabel}>RETURNING</div>
                      <div style={overviewStyles.statValue}>{platformAnalytics.overview?.returningUsers?.toLocaleString() || 0}</div>
                      <div style={overviewStyles.statDescription}>Returning visitors</div>
                    </div>
                  </div>

                  {/* Sessions Over Time Chart */}
                  <div style={overviewStyles.chartCard}>
                    <div style={overviewStyles.chartHeader}>
                      <div>
                        <div style={overviewStyles.sectionLabel}>T R A F F I C</div>
                        <h3 style={overviewStyles.chartTitle}>Sessions & Page Views Over Time</h3>
                      </div>
                      <span style={overviewStyles.chartPeriod}>Last 30 days</span>
                    </div>
                    <div style={{ height: '300px', marginTop: '24px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={platformAnalytics.sessions_over_time || []}>
                          <defs>
                            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#171717" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#171717" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorPV" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#71717a" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#71717a" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                          <YAxis stroke="#71717a" fontSize={12} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }} />
                          <Legend />
                          <Area type="monotone" dataKey="sessions" stroke="#171717" fillOpacity={1} fill="url(#colorSessions)" name="Sessions" />
                          <Area type="monotone" dataKey="pageViews" stroke="#71717a" fillOpacity={1} fill="url(#colorPV)" name="Page Views" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Two Column: Top Pages + Device Breakdown */}
                  <div style={overviewStyles.twoColumnGrid}>
                    {/* Top Pages */}
                    <div style={overviewStyles.card}>
                      <div style={overviewStyles.cardHeader}>
                        <div style={overviewStyles.sectionLabel}>P A G E S</div>
                        <h3 style={overviewStyles.cardTitle}>Top Pages</h3>
                      </div>
                      <div style={{ height: '300px', marginTop: '16px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={platformAnalytics.top_pages || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="page" stroke="#71717a" fontSize={11} angle={-45} textAnchor="end" height={80} />
                            <YAxis stroke="#71717a" fontSize={12} />
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }} />
                            <Bar dataKey="views" fill="#171717" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Traffic Sources Pie */}
                    <div style={overviewStyles.card}>
                      <div style={overviewStyles.cardHeader}>
                        <div style={overviewStyles.sectionLabel}>S O U R C E S</div>
                        <h3 style={overviewStyles.cardTitle}>Traffic Sources</h3>
                      </div>
                      <div style={{ height: '300px', marginTop: '16px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={platformAnalytics.traffic_channels || []}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ source, percentage }) => `${source}: ${percentage}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="sessions"
                            >
                              {(platformAnalytics.traffic_channels || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#171717', '#52525b', '#a1a1aa', '#d4d4d4', '#e5e5e5'][index % 5]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Device Categories */}
                  <div style={overviewStyles.chartCard}>
                    <div style={overviewStyles.chartHeader}>
                      <div>
                        <div style={overviewStyles.sectionLabel}>D E V I C E S</div>
                        <h3 style={overviewStyles.chartTitle}>Device Categories</h3>
                      </div>
                    </div>
                    <div style={{ height: '300px', marginTop: '24px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={platformAnalytics.devices || platformAnalytics.device_categories || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" stroke="#71717a" fontSize={12} />
                          <YAxis dataKey="device" type="category" stroke="#71717a" fontSize={12} width={80} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }} />
                          <Bar dataKey="sessions" fill="#171717" radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Engagement Funnel */}
                  <div style={overviewStyles.chartCard}>
                    <div style={overviewStyles.chartHeader}>
                      <div>
                        <div style={overviewStyles.sectionLabel}>F U N N E L</div>
                        <h3 style={overviewStyles.chartTitle}>Engagement Funnel</h3>
                      </div>
                      <span style={overviewStyles.chartPeriod}>All time</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                      {(() => {
                        const funnel = [
                          { label: 'Page Views', value: platformAnalytics.engagement?.total_views || 0, color: '#171717' },
                          { label: 'Total Events', value: platformAnalytics.engagement?.total_events || 0, color: '#3f3f46' },
                          { label: 'Try-On Uses', value: platformAnalytics.engagement?.tryon_uses || 0, color: '#52525b' },
                          { label: 'Products Saved', value: platformAnalytics.engagement?.saved_products || 0, color: '#71717a' },
                        ];
                        const maxVal = Math.max(...funnel.map(f => f.value), 1);
                        return funnel.map((step, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ width: '140px', fontSize: '13px', fontWeight: '500', color: '#171717', textAlign: 'right', flexShrink: 0 }}>
                              {step.label}
                            </div>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <div style={{
                                height: '36px',
                                width: `${Math.max((step.value / maxVal) * 100, 4)}%`,
                                background: step.color,
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                paddingRight: '12px',
                                transition: 'width 0.5s ease',
                              }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                                  {step.value.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            {i > 0 && funnel[0].value > 0 && (
                              <div style={{ width: '60px', fontSize: '12px', color: '#71717a', textAlign: 'right', flexShrink: 0 }}>
                                {((step.value / funnel[0].value) * 100).toFixed(1)}%
                              </div>
                            )}
                            {i === 0 && (
                              <div style={{ width: '60px', fontSize: '12px', color: '#71717a', textAlign: 'right', flexShrink: 0 }}>100%</div>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                <div style={overviewStyles.emptyState}>
                  <p style={overviewStyles.emptyText}>No analytics data available yet. Page view tracking is active — data will populate as users visit the platform.</p>
                </div>
              )}
            </>
          )}

          {activeSection === 'notifications' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>N O T I F I C A T I O N S</div>
                  <h2 style={sectionStyles.title}>Send Notifications</h2>
                  <p style={sectionStyles.subtitle}>Send messages to brand partners</p>
                </div>
              </div>

              {/* Send Notification Form */}
              <div style={{ background: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#171717', marginBottom: '20px' }}>New Notification</h3>
                
                {notificationFormError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                    {notificationFormError}
                  </div>
                )}
                {notificationFormSuccess && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                    {notificationFormSuccess}
                  </div>
                )}

                <form onSubmit={handleSendNotification}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#171717', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.send_to_all}
                        onChange={(e) => setNotificationForm(prev => ({ ...prev, send_to_all: e.target.checked, brand_id: '' }))}
                      />
                      Send to all active brands
                    </label>
                  </div>

                  {!notificationForm.send_to_all && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#171717', marginBottom: '6px' }}>Select Brand</label>
                      <select
                        value={notificationForm.brand_id}
                        onChange={(e) => setNotificationForm(prev => ({ ...prev, brand_id: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', background: '#ffffff' }}
                      >
                        <option value="">Choose a brand...</option>
                        {brands.map(b => (
                          <option key={b.id} value={b.id}>{b.name} ({b.email})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#171717', marginBottom: '6px' }}>Priority</label>
                    <select
                      value={notificationForm.priority}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, priority: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', background: '#ffffff' }}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#171717', marginBottom: '6px' }}>Title</label>
                    <input
                      type="text"
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Notification title"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#171717', marginBottom: '6px' }}>Message</label>
                    <textarea
                      value={notificationForm.message}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Write your notification message..."
                      rows={4}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sendingNotification}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '12px 24px', background: '#171717', color: '#ffffff',
                      border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
                      cursor: sendingNotification ? 'not-allowed' : 'pointer', opacity: sendingNotification ? 0.6 : 1,
                    }}
                  >
                    <Send style={{ width: '16px', height: '16px' }} />
                    {sendingNotification ? 'Sending...' : 'Send Notification'}
                  </button>
                </form>
              </div>

              {/* Recent Notifications */}
              <div style={{ background: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '12px', padding: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#171717', marginBottom: '20px' }}>
                  Recent Notifications ({adminNotifications.length})
                </h3>
                {adminNotifications.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#71717a' }}>
                    <Bell style={{ width: '40px', height: '40px', color: '#d4d4d4', marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>No notifications sent yet</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {adminNotifications.map((n) => (
                      <div key={n.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        padding: '14px 16px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0',
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#171717' }}>{n.title}</span>
                            <span style={{
                              fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: '500',
                              background: n.priority === 'high' ? '#fef2f2' : n.priority === 'normal' ? '#eff6ff' : '#f5f5f5',
                              color: n.priority === 'high' ? '#ef4444' : n.priority === 'normal' ? '#3b82f6' : '#71717a',
                            }}>
                              {n.priority}
                            </span>
                            {n.is_read && (
                              <span style={{ fontSize: '11px', color: '#16a34a' }}>Read</span>
                            )}
                          </div>
                          <p style={{ fontSize: '13px', color: '#52525b', margin: '0 0 4px 0', lineHeight: 1.4 }}>{n.message}</p>
                          <div style={{ fontSize: '12px', color: '#a1a1aa' }}>
                            To: <strong>{n.brand_name}</strong> · {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'spotlight' && (
            <>
              <div style={sectionStyles.header}>
                <div>
                  <div style={sectionStyles.label}>S P O T L I G H T</div>
                  <h2 style={sectionStyles.title}>Brand Spotlight Settings</h2>
                  <p style={sectionStyles.subtitle}>Control which brands appear in the spotlight section on the homepage</p>
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                {spotlightMessage && (
                  <div style={{
                    background: spotlightMessage.includes('success') ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${spotlightMessage.includes('success') ? '#bbf7d0' : '#fecaca'}`,
                    color: spotlightMessage.includes('success') ? '#16a34a' : '#ef4444',
                    padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
                  }}>
                    {spotlightMessage}
                  </div>
                )}

                {/* Enable/Disable Toggle */}
                <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#171717', margin: '0 0 4px 0' }}>Show Spotlight Section</h3>
                    <p style={{ fontSize: '13px', color: '#71717a', margin: 0 }}>
                      When enabled, the brand spotlight banner will appear on the homepage
                    </p>
                  </div>
                  <button
                    onClick={() => setSpotlightSettings(prev => ({ ...prev, spotlight_enabled: !prev.spotlight_enabled }))}
                    style={{
                      width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                      background: spotlightSettings.spotlight_enabled ? '#171717' : '#d4d4d4',
                      position: 'relative', transition: 'background 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '3px',
                      left: spotlightSettings.spotlight_enabled ? '27px' : '3px',
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>
                </div>

                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
                  {/* Brand Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                      Featured Brands
                    </label>
                    <p style={{ fontSize: '12px', color: '#71717a', margin: '0 0 8px 0' }}>
                      Enter brand names separated by commas. Leave empty to auto-select from newly launched brands.
                    </p>
                    <input
                      type="text"
                      value={spotlightBrandInput}
                      onChange={(e) => setSpotlightBrandInput(e.target.value)}
                      placeholder="e.g. Brand A, Brand B, Brand C"
                      style={{
                        width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                        borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                      }}
                    />
                    {/* Quick-pick from existing brands */}
                    {brands.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ fontSize: '12px', color: '#71717a', margin: '0 0 6px 0' }}>Quick add:</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {brands.filter(b => b.is_active).slice(0, 20).map(b => {
                            const isSelected = spotlightBrandInput.split(',').map(n => n.trim().toLowerCase()).includes(b.name.toLowerCase());
                            return (
                              <button
                                key={b.id}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    const updated = spotlightBrandInput
                                      .split(',')
                                      .map(n => n.trim())
                                      .filter(n => n.toLowerCase() !== b.name.toLowerCase())
                                      .join(', ');
                                    setSpotlightBrandInput(updated);
                                  } else {
                                    const current = spotlightBrandInput.trim();
                                    setSpotlightBrandInput(current ? `${current}, ${b.name}` : b.name);
                                  }
                                }}
                                style={{
                                  padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '500',
                                  border: isSelected ? '1px solid #171717' : '1px solid #e5e5e5',
                                  background: isSelected ? '#171717' : '#fff',
                                  color: isSelected ? '#fff' : '#52525b',
                                  cursor: 'pointer', transition: 'all 0.15s',
                                }}
                              >
                                {b.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Spotlight Limit */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                      Max Brands to Show
                    </label>
                    <p style={{ fontSize: '12px', color: '#71717a', margin: '0 0 8px 0' }}>
                      Maximum number of brands in the spotlight rotation (1–20)
                    </p>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={spotlightSettings.spotlight_limit}
                      onChange={(e) => setSpotlightSettings(prev => ({ ...prev, spotlight_limit: parseInt(e.target.value) || 1 }))}
                      style={{
                        width: '120px', padding: '10px 12px', border: '1px solid #e5e5e5',
                        borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Spotlight Days (for auto mode) */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                      New Launch Window (days)
                    </label>
                    <p style={{ fontSize: '12px', color: '#71717a', margin: '0 0 8px 0' }}>
                      When no specific brands are set, auto-select brands launched within this many days (1–90)
                    </p>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={spotlightSettings.spotlight_days}
                      onChange={(e) => setSpotlightSettings(prev => ({ ...prev, spotlight_days: parseInt(e.target.value) || 7 }))}
                      style={{
                        width: '120px', padding: '10px 12px', border: '1px solid #e5e5e5',
                        borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Upcoming Brand Spotlight */}
                  <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#171717', margin: '0 0 4px 0' }}>Upcoming Brand Spotlight</h3>
                        <p style={{ fontSize: '13px', color: '#71717a', margin: 0 }}>
                          Feature a brand that hasn't launched yet — enter details manually
                        </p>
                      </div>
                      <button
                        onClick={() => setSpotlightSettings(prev => ({ ...prev, upcoming_enabled: !prev.upcoming_enabled }))}
                        style={{
                          width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                          background: spotlightSettings.upcoming_enabled ? '#171717' : '#d4d4d4',
                          position: 'relative', transition: 'background 0.2s',
                          flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: '3px',
                          left: spotlightSettings.upcoming_enabled ? '27px' : '3px',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </button>
                    </div>

                    {spotlightSettings.upcoming_enabled && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                            Brand Name *
                          </label>
                          <input
                            type="text"
                            value={spotlightSettings.upcoming_brand_name}
                            onChange={(e) => setSpotlightSettings(prev => ({ ...prev, upcoming_brand_name: e.target.value }))}
                            maxLength={100}
                            placeholder="e.g. Kashmir Couture"
                            style={{
                              width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                              borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                            Description
                          </label>
                          <textarea
                            value={spotlightSettings.upcoming_brand_description}
                            onChange={(e) => setSpotlightSettings(prev => ({ ...prev, upcoming_brand_description: e.target.value }))}
                            placeholder="A short description of the upcoming brand..."
                            rows={3}
                            style={{
                              width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                              borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical',
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                            Brand Logo
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleSpotlightLogoFileSelect}
                              style={{ display: 'none' }}
                              id="spotlight-logo-upload"
                              disabled={uploadingSpotlightLogo}
                            />
                            <label
                              htmlFor="spotlight-logo-upload"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 16px',
                                background: '#ffffff',
                                border: '1px solid #e5e5e5',
                                borderRadius: '8px',
                                color: '#171717',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: uploadingSpotlightLogo ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s',
                              }}
                            >
                              {uploadingSpotlightLogo ? 'Uploading...' : 'Choose Logo Image'}
                            </label>
                            <span style={{ fontSize: '12px', color: '#a1a1aa' }}>
                              JPEG, PNG, WebP or GIF. Max 5MB.
                            </span>
                          </div>
                          {spotlightSettings.upcoming_brand_logo_url && (
                            <div style={{ marginTop: '8px' }}>
                              <img
                                src={spotlightSettings.upcoming_brand_logo_url}
                                alt="Preview"
                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e5e5' }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                            Launch Date
                          </label>
                          <input
                            type="date"
                            value={spotlightSettings.upcoming_brand_launch_date || ''}
                            onChange={(e) => setSpotlightSettings(prev => ({ ...prev, upcoming_brand_launch_date: e.target.value }))}
                            style={{
                              width: '200px', padding: '10px 12px', border: '1px solid #e5e5e5',
                              borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '0' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                            Subtitle Text
                          </label>
                          <input
                            type="text"
                            value={spotlightSettings.upcoming_subtitle}
                            onChange={(e) => setSpotlightSettings(prev => ({ ...prev, upcoming_subtitle: e.target.value }))}
                            maxLength={200}
                            placeholder="Be the first to discover this brand when it launches"
                            style={{
                              width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                              borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Editable Spotlight Container Text */}
                  <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#171717', marginBottom: '16px' }}>
                      Spotlight Container Text
                    </h3>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                        Title
                      </label>
                      <input
                        type="text"
                        value={spotlightSettings.spotlight_title}
                        onChange={(e) => setSpotlightSettings(prev => ({ ...prev, spotlight_title: e.target.value }))}
                        maxLength={100}
                        placeholder="Brand Spotlight"
                        style={{
                          width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                          borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                        Subtitle
                      </label>
                      <input
                        type="text"
                        value={spotlightSettings.spotlight_subtitle}
                        onChange={(e) => setSpotlightSettings(prev => ({ ...prev, spotlight_subtitle: e.target.value }))}
                        maxLength={200}
                        placeholder="Discover emerging brands from Kashmir"
                        style={{
                          width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                          borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#171717', marginBottom: '6px' }}>
                        Call-to-Action Button Text
                      </label>
                      <input
                        type="text"
                        value={spotlightSettings.spotlight_cta_text}
                        onChange={(e) => setSpotlightSettings(prev => ({ ...prev, spotlight_cta_text: e.target.value }))}
                        maxLength={50}
                        placeholder="Explore Brand"
                        style={{
                          width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5',
                          borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  {/* Current Status */}
                  <div style={{
                    background: '#fafafa', borderRadius: '8px', padding: '14px 16px', marginBottom: '24px',
                    border: '1px solid #f0f0f0',
                  }}>
                    <p style={{ fontSize: '13px', color: '#52525b', margin: 0, lineHeight: 1.5 }}>
                      <strong>Current config:</strong>{' '}
                      Spotlight is <strong>{spotlightSettings.spotlight_enabled ? 'enabled' : 'disabled'}</strong>.{' '}
                      {spotlightBrandInput.trim()
                        ? <>Featuring: <strong>{spotlightBrandInput}</strong></>
                        : <>Auto-selecting from brands launched in the last <strong>{spotlightSettings.spotlight_days} days</strong></>
                      }.{' '}
                      Max <strong>{spotlightSettings.spotlight_limit}</strong> brand{spotlightSettings.spotlight_limit !== 1 ? 's' : ''} in rotation.
                      {spotlightSettings.upcoming_enabled && spotlightSettings.upcoming_brand_name && (
                        <><br /><strong>Upcoming:</strong> <strong>{spotlightSettings.upcoming_brand_name}</strong>{spotlightSettings.upcoming_brand_launch_date ? <> launching <strong>{spotlightSettings.upcoming_brand_launch_date}</strong></> : ''}</>
                      )}
                    </p>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={handleSaveSpotlightSettings}
                    disabled={spotlightSaving}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '12px 24px', background: '#171717', color: '#ffffff',
                      border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
                      cursor: spotlightSaving ? 'not-allowed' : 'pointer',
                      opacity: spotlightSaving ? 0.6 : 1,
                    }}
                  >
                    <Star style={{ width: '16px', height: '16px' }} />
                    {spotlightSaving ? 'Saving...' : 'Save Spotlight Settings'}
                  </button>
                </div>
              </div>
            </>
          )}
        </main>

        {/* Portal Footer */}
        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.footerBrand}>
              <span style={styles.footerLogo}>Y</span>
              <span style={styles.footerName}>YEMBERZAL</span>
              <span style={styles.footerDivider}>|</span>
              <span style={styles.footerTagline}>Admin Portal</span>
            </div>
            <div style={styles.footerLinks}>
              <Link to="/" style={styles.footerLink}>Main Site</Link>
              <a href="mailto:yemberzal.me@gmail.com" style={styles.footerLink}>Support</a>
            </div>
            <div style={styles.footerCopyright}>
              © {new Date().getFullYear()} Yemberzal. Kashmir-first fashion discovery.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    background: '#ffffff',
  },
  sidebar: {
    width: '240px',
    minWidth: '240px',
    background: '#171717',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    overflowY: 'auto',
  },
  sidebarHeader: {
    padding: '32px 24px',
  },
  logoContainer: {
    width: '100%',
    height: '60px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  logoImage: {
    backgroundColor: 'transparent',
    background: 'none',
    width: '120%',
    height: '120%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    transform: 'scale(1.2)',
  },
  adminLabel: {
    fontSize: '11px',
    letterSpacing: '0.3em',
    color: '#71717a',
    textAlign: 'center',
    margin: 0,
    fontWeight: '500',
  },
  nav: {
    padding: '16px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    marginBottom: '4px',
    borderRadius: '8px',
    color: '#ffffff',
    background: 'transparent',
    border: 'none',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    width: '100%',
    textAlign: 'left',
  },
  navItemActive: {
    background: '#ffffff',
    color: '#171717',
  },
  sidebarFooter: {
    padding: '24px',
    borderTop: '1px solid #262626',
  },
  accountSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  accountLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    color: '#ffffff',
    background: 'transparent',
    border: 'none',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    width: '100%',
    textAlign: 'left',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    color: '#ffffff',
    background: 'transparent',
    border: 'none',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    width: '100%',
    textAlign: 'left',
  },
  mainWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '240px',
    height: '100vh',
    overflowY: 'auto',
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '32px 48px 24px',
    borderBottom: '1px solid #e5e5e5',
  },
  topbarTitle: {
    fontSize: '32px',
    fontWeight: '400',
    color: '#171717',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  topbarUser: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userLabel: {
    fontSize: '12px',
    letterSpacing: '0.1em',
    color: '#71717a',
    fontWeight: '600',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: '#171717',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600',
  },
  main: {
    flex: 1,
    padding: '48px',
    background: '#ffffff',
  },
  footer: {
    background: '#fafafa',
    borderTop: '1px solid #e5e5e5',
    padding: '32px 48px',
  },
  footerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerLogo: {
    width: '28px',
    height: '28px',
    background: '#171717',
    color: '#ffffff',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
  },
  footerName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#171717',
    letterSpacing: '0.1em',
  },
  footerDivider: {
    color: '#d4d4d4',
    margin: '0 4px',
  },
  footerTagline: {
    fontSize: '13px',
    color: '#71717a',
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  footerLink: {
    fontSize: '13px',
    color: '#71717a',
    textDecoration: 'none',
    transition: 'color 0.15s',
  },
  footerCopyright: {
    fontSize: '12px',
    color: '#a1a1aa',
    width: '100%',
    textAlign: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e5e5e5',
    marginTop: '16px',
  },
};

const sectionStyles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '400',
    color: '#171717',
    margin: '8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: 0,
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#171717',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '260px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'admin-spin 0.8s linear infinite',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid #e5e5e5',
  },
  tab: {
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderBottom: 'none',
    color: '#71717a',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginBottom: '-1px',
  },
  tabActive: {
    color: '#171717',
    borderBottom: '2px solid #171717',
  },
};

const overviewStyles = {
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  statCard: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    padding: '24px',
    transition: 'all 0.2s',
    minHeight: '140px',
    display: 'flex',
    flexDirection: 'column',
  },
  statHeader: {
    marginBottom: '16px',
  },
  statLabel: {
    fontSize: '11px',
    letterSpacing: '0.1em',
    color: '#a1a1aa',
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '400',
    color: '#171717',
    marginBottom: '8px',
    letterSpacing: '-0.02em',
  },
  statDescription: {
    fontSize: '13px',
    color: '#71717a',
    marginTop: 'auto',
  },
  chartCard: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionLabel: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  chartTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  chartPeriod: {
    fontSize: '13px',
    color: '#71717a',
  },
  chartContainer: {
    minHeight: '240px',
  },
  chart: {
    height: '220px',
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'end',
    gap: '12px',
    paddingTop: '10px',
  },
  chartBar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    minWidth: '32px',
    flex: 1,
  },
  chartFill: {
    width: '100%',
    background: '#171717',
    borderRadius: '4px 4px 0 0',
    transition: 'height 0.3s ease',
    minHeight: '4px',
  },
  chartLabel: {
    fontSize: '11px',
    color: '#71717a',
    textAlign: 'center',
  },
  emptyChart: {
    height: '220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    marginBottom: '24px',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
  },
  cardHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  listItemName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
  },
  listItemValue: {
    fontSize: '14px',
    color: '#71717a',
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '20px',
  },
  productCard: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.2s',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  productCardHover: {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    borderColor: '#d4d4d4',
    transform: 'translateY(-2px)',
  },
  productImage: {
    width: '100%',
    height: '180px',
    overflow: 'hidden',
    background: '#f5f5f5',
    position: 'relative',
  },
  productImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  productInfo: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
  },
  productTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '40px',
  },
  productPrice: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#171717',
    marginTop: 'auto',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '14px',
    color: '#a1a1aa',
    margin: 0,
  },
};

const formStyles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  cardHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
};

const tableStyles = {
  container: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    overflow: 'hidden',
    marginTop: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    background: '#fafafa',
  },
  th: {
    padding: '16px 20px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#71717a',
    borderBottom: '1px solid #e5e5e5',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '14px',
    color: '#171717',
  },
  tr: {
    transition: 'background 0.15s',
  },
  emptyCell: {
    textAlign: 'center',
    color: '#a1a1aa',
    padding: '48px 20px',
    fontSize: '14px',
  },
  brandCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  brandAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontSize: '16px',
    fontWeight: '600',
    color: '#171717',
    flexShrink: 0,
  },
  brandName: {
    margin: 0,
    fontWeight: '600',
    fontSize: '14px',
    color: '#171717',
  },
  brandEmail: {
    margin: '4px 0 0',
    fontSize: '13px',
    color: '#71717a',
  },
  mutedText: {
    color: '#71717a',
    fontSize: '13px',
    margin: '4px 0 0',
  },
  statusBadge: {
    display: 'inline-flex',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    background: '#f5f5f5',
    color: '#71717a',
  },
  statusBadgeActive: {
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#10b981',
  },
  statusBadgePending: {
    background: 'rgba(245, 158, 11, 0.15)',
    color: '#f59e0b',
  },
  statusBadgeSuspended: {
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#ef4444',
  },
  actionBtns: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    background: '#ffffff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#171717',
    transition: 'all 0.15s',
  },
  actionBtnDanger: {
    color: '#ef4444',
    borderColor: 'rgba(239, 68, 68, 0.2)',
  },
  actionBtnSuccess: {
    color: '#10b981',
    borderColor: 'rgba(16, 185, 129, 0.2)',
  },
};


export default AdminDashboard;
